import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Play, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { VideoModal } from "@/components/VideoModal";

const SourceBadge = ({ source }) => (
  <span className="font-mono2 text-[0.6rem] tracking-[0.18em] uppercase bg-ink text-paper px-2 py-1">
    {source}
  </span>
);

const Home = () => {
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState("All");
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/posts").then(({ data }) => setPosts(data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (category === "All" ? posts : posts.filter((p) => p.category === category)),
    [posts, category]
  );

  const lead = filtered.find((p) => p.featured) || filtered[0];
  const rest = filtered.filter((p) => p.id !== lead?.id);
  const videos = rest.filter((p) => p.content_type === "video");
  const articles = rest.filter((p) => p.content_type !== "video");

  const openPost = (p) => {
    if (p.content_type === "video" && p.youtube_id) setVideo(p);
    else window.open(p.url, "_blank", "noopener");
  };

  return (
    <div className="bg-paper min-h-screen">
      <Header activeCategory={category} onCategory={setCategory} />

      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16 py-10">
        {/* Section intro */}
        <div className="flex items-end justify-between border-b-2 border-ink pb-4 mb-10">
          <div>
            <div className="overline-label text-signal mb-2">// {category} Dispatches</div>
            <h2 className="font-display text-3xl sm:text-4xl font-black text-ink tracking-tight">
              The Curated Feed
            </h2>
          </div>
          <span className="hidden sm:block font-mono2 text-xs text-muted-ink">{filtered.length} FILES</span>
        </div>

        {loading && <p className="font-mono2 text-muted-ink text-sm">// Decrypting archive…</p>}

        {!loading && !lead && (
          <p className="font-body text-muted-ink py-20 text-center">No dispatches in this section yet.</p>
        )}

        {/* Lead story */}
        {lead && (
          <article
            data-testid="featured-lead"
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 fade-up cursor-pointer group"
            onClick={() => openPost(lead)}
          >
            <div className="lg:col-span-7 relative overflow-hidden grain-overlay">
              <img
                src={lead.image_url}
                alt={lead.title}
                className="w-full h-[320px] sm:h-[460px] object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <span className="absolute top-4 left-4"><SourceBadge source={lead.source} /></span>
              {lead.content_type === "video" && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="bg-signal/90 text-white rounded-full p-5 group-hover:scale-110 transition-transform">
                    <Play size={28} fill="white" />
                  </span>
                </span>
              )}
            </div>
            <div className="lg:col-span-5 flex flex-col justify-center">
              <div className="overline-label text-signal mb-3">{lead.category} — Lead Investigation</div>
              <h3 className="font-display text-3xl sm:text-5xl font-black text-ink leading-[1.05] tracking-tight">
                {lead.title}
              </h3>
              <p className="font-body text-lg text-muted-ink mt-5 leading-relaxed">{lead.summary}</p>
              <div className="flex items-center gap-4 mt-6">
                <span className="font-mono2 text-xs text-ink flex items-center gap-2">
                  {lead.content_type === "video" ? <Play size={14} /> : <Clock size={14} />} {lead.read_time}
                </span>
                <span className="font-mono2 text-xs text-signal flex items-center gap-1 link-underline">
                  {lead.content_type === "video" ? "Watch Briefing" : "Read at Source"} <ArrowUpRight size={14} />
                </span>
              </div>
            </div>
          </article>
        )}

        {/* Article grid */}
        {articles.length > 0 && (
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 mb-20">
            {articles.map((p, idx) => (
              <article
                key={p.id}
                data-testid={`post-card-${p.id}`}
                onClick={() => openPost(p)}
                className="group cursor-pointer border-t-2 border-ink pt-5 fade-up"
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                <div className="overflow-hidden mb-4">
                  <img
                    src={p.image_url}
                    alt={p.title}
                    className="w-full h-52 object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex items-center justify-between mb-3">
                  <SourceBadge source={p.source} />
                  <span className="overline-label text-muted-ink">{p.category}</span>
                </div>
                <h3 className="font-display text-xl font-bold text-ink leading-snug group-hover:text-signal transition-colors">
                  {p.title}
                </h3>
                <p className="font-body text-muted-ink mt-2 line-clamp-3">{p.summary}</p>
                <div className="flex items-center gap-3 mt-4">
                  <span className="font-mono2 text-[0.65rem] text-muted-ink flex items-center gap-1.5"><Clock size={12} /> {p.read_time}</span>
                  <span className="font-mono2 text-[0.65rem] text-signal flex items-center gap-1 ml-auto">SOURCE <ArrowUpRight size={12} /></span>
                </div>
              </article>
            ))}
          </section>
        )}

        {/* Video archive */}
        {videos.length > 0 && (
          <section data-testid="video-section">
            <div className="flex items-end justify-between border-b-2 border-ink pb-4 mb-10">
              <div>
                <div className="overline-label text-signal mb-2">// Visual Intelligence</div>
                <h2 className="font-display text-3xl sm:text-4xl font-black text-ink tracking-tight">Video Archive</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {videos.map((p) => (
                <article
                  key={p.id}
                  data-testid={`video-card-${p.id}`}
                  onClick={() => openPost(p)}
                  className="group cursor-pointer relative overflow-hidden grain-overlay"
                >
                  <img src={p.image_url} alt={p.title} className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  <span className="absolute top-4 left-4"><SourceBadge source={p.source} /></span>
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="bg-signal text-white rounded-full p-4 group-hover:scale-110 transition-transform">
                      <Play size={24} fill="white" />
                    </span>
                  </span>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="font-display text-xl font-bold text-white leading-snug">{p.title}</h3>
                    <span className="font-mono2 text-[0.65rem] text-white/70 tracking-[0.15em] uppercase mt-1 block">{p.read_time}</span>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
      <VideoModal post={video} onClose={() => setVideo(null)} />
    </div>
  );
};

export default Home;
